// Capexara AI — Case studies (anonymised)
export const CASES = [
  {
    slug: 'national-refiner-cpct',
    client: 'National Refiner',
    industry: 'Oil & Gas · Downstream',
    metric: '₹412 Cr',
    label: 'saved via CPCT deployment',
    detail: 'Integrated EVM + 4D Timeliner across 3 refinery FCC revamp projects; reduced schedule slip by 21%, claims exposure by 18%.',
    viz: 'spark',
    products: ['drishti', 'sutradhar', 'samgraha'],
    overview:
      'A top-3 national refiner running three concurrent FCC revamp projects faced chronic schedule slippage and mounting contractor claims. The CPCT (Capital Projects Control Tower) deployment unified EVM, 4D schedule visualisation, claims pipeline, and HSE telemetry into a single pane of glass across all three sites — with portfolio rollup for the CXO team.',
    challenge:
      'Each refinery had independent schedule systems (two on legacy schedulers, one on a different scheduler). Claims were being logged in email trails. HSE data sat in a separate ERP. Leadership had no single view of portfolio health, and downstream EPC contractors were flagging disruption claims that the owner could not rebut in real time.',
    approach: [
      'Deployed Panorix (Control Tower) as the portfolio-level pane of glass',
      'Connected legacy schedulers via bi-directional sync; normalised WBS/CBS across sites',
      'Enabled 4D Timeliner on Autodesk Forge 3D models for live construction sequence',
      'Stood up Cognara (GenAI agent) on FIDIC contracts to draft counter-claim responses in <36h',
      'ProcureX contracts lifecycle for clause-library enforcement on change orders',
    ],
    outcome: [
      { metric: '₹412 Cr', label: 'savings realised over 18 months' },
      { metric: '-21%', label: 'schedule-slip reduction YoY' },
      { metric: '-18%', label: 'contractor claims exposure' },
      { metric: '11-day → 36h', label: 'claim-response cycle' },
    ],
    quote:
      'The control tower has changed how our executive committee runs portfolio reviews. We now argue about the plan, not about whose spreadsheet is right.',
    quoteAttrib: 'Portfolio Director · National Refiner (confidential)',
  },
  {
    slug: 'global-epc-feed-rachana',
    client: 'Global EPC (Top 10)',
    industry: 'Power · Combined-Cycle',
    metric: '38%',
    label: 'FEED cycle-time reduction',
    detail: 'Deployed Formigen for P&ID OCR and 3D plant-design layout optimization across 4 concurrent bids; won 2 of 3.',
    viz: 'donut',
    products: ['rachana', 'grantha', 'sutradhar'],
    overview:
      'A global top-10 EPC contractor pursuing four concurrent CCGT bids needed to compress FEED duration to preserve bidding margin while delivering higher-fidelity proposals. Formigen ingested legacy P&IDs, optimised plant layout via reinforcement learning, and exported directly into incumbent 3D plant-design suites.',
    challenge:
      'FEED teams were overloaded — concurrent bid volume had doubled, yet headcount remained flat. Manual P&ID digitisation was the single largest cycle-time bottleneck, consuming 6 weeks on legacy-plant reference bids.',
    approach: [
      'Formigen P&ID OCR + symbol detection on 1,200 legacy drawings',
      'Auto-generated equipment, line and instrument lists for bid pricing',
      'Reinforcement-learning plant layout optimiser with HSE clearance constraints',
      'Docuweave EDMS for revision control across the distributed FEED team',
      'Cognara agent for code-compliance (API 650/653, ASME B31.3) checks pre-IFR',
    ],
    outcome: [
      { metric: '-38%', label: 'FEED cycle-time reduction' },
      { metric: '2 of 3', label: 'bids won on RL-optimised layout' },
      { metric: '1,200', label: 'legacy P&IDs digitised in 3 weeks' },
      { metric: '-9%', label: 'total pipe-run length (layout optimisation)' },
    ],
    quote:
      'Formigen turned FEED from a constraint into an edge. We are bidding on deals we would have declined eighteen months ago.',
    quoteAttrib: 'VP Engineering · Global EPC (confidential)',
  },
  {
    slug: 'metro-spv-claims-samgraha',
    client: 'Metro SPV',
    industry: 'Infrastructure · Rails',
    metric: '22%',
    label: 'claims exposure reduction',
    detail: 'ProcureX clause-library and GenAI counter-claim drafting closed 127 pending contractor claims in 11 weeks.',
    viz: 'bars',
    products: ['samgraha', 'sutradhar', 'vidhana'],
    overview:
      'A mega metro SPV sat on 127 pending contractor claims aggregating to ~₹640 Cr exposure. The legal and contracts teams were firefighting. ProcureX and Cognara jointly rebuilt the claims pipeline with clause-library enforcement and GenAI-assisted counter-claim drafting.',
    challenge:
      'Contracts were authored by multiple outside counsel with inconsistent clause structures. Claim responses were missing SLAs. Counter-claim drafting was taking 14-20 days per claim, and the SPV was compounding exposure through procedural defaults.',
    approach: [
      'Luminex re-engineered the claims-response process (as-is to to-be in 4 weeks)',
      'ProcureX clause library enforced on all change orders and new contracts',
      'Cognara drafted counter-claim narratives with clause-level contract citations',
      'SLA-tracked claims pipeline with auto-escalation on breach',
    ],
    outcome: [
      { metric: '-22%', label: 'claims-exposure reduction' },
      { metric: '127 closed', label: 'pending claims resolved in 11 weeks' },
      { metric: '14-20 days → 36h', label: 'counter-claim drafting cycle' },
      { metric: '100%', label: 'new contracts on risk-weighted clauses' },
    ],
    quote:
      'We went from being on the defensive to being able to set terms. The AI does not replace our lawyers — it multiplies them.',
    quoteAttrib: 'Head of Contracts · Metro SPV (confidential)',
  },
];

export const getCaseBySlug = (slug) => CASES.find((c) => c.slug === slug);
